'use client';

/**
 * IntakeSubmissionSummary Component
 *
 * Displays submitted intake form data in a readonly format.
 * Shows questions and answers grouped by sections.
 *
 * @see Issue #475 - Intake Workflow
 */

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Rating from '@mui/material/Rating';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type {
  IntakeCondition,
  IntakeFieldTemplate,
  IntakeFlowTemplate,
  IntakeStepTemplate,
} from '@ops-matrix/shared-types';
import { useMemo } from 'react';
import { type NeonAccent, NeonAccentKey } from '../../theme/palette';
import { GlassCard } from '../GlassCard';
import { getI18nString } from './intake-utils';

// ============================================================================
// Types
// ============================================================================

export interface IntakeSubmissionSummaryProps {
  /**
   * Intake flow template (defines the questions)
   */
  template: IntakeFlowTemplate;

  /**
   * Submitted form values
   */
  values: Record<string, unknown>;

  /**
   * Neon accent color for cards
   */
  neonAccent?: NeonAccent;

  /**
   * Whether to show section headers
   */
  showSections?: boolean;

  /**
   * Whether to show empty/unanswered fields
   */
  showEmptyFields?: boolean;

  /**
   * Additional class name
   */
  className?: string;

  /**
   * Title to display at the top
   */
  title?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const SectionContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const SectionNumber = styled(Box)(({ theme }) => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  fontSize: '0.8rem',
  flexShrink: 0,
  background: theme.palette.success.main,
  color: theme.palette.success.contrastText,
}));

const SectionTitle = styled(Typography)(() => ({
  fontSize: '1rem',
  fontWeight: 600,
}));

const FieldRow = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-of-type': {
    borderBottom: 'none',
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
  fontWeight: 500,
}));

const FieldValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.9375rem',
  color: theme.palette.text.primary,
}));

const EmptyValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.disabled,
  fontStyle: 'italic',
}));

const AddressValue = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
}));

const ChipGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
}));

// ============================================================================
// Value Formatters
// ============================================================================

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatPercentage = (value: number): string => {
  return `${value}%`;
};

const formatDate = (value: string): string => {
  try {
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return value;
  }
};

const formatSSN = (value: string): string => {
  // Mask SSN for security (show only last 4 digits)
  const digits = value.replace(/\D/g, '');
  if (digits.length === 9) {
    return `***-**-${digits.slice(-4)}`;
  }
  return '***-**-****';
};

// ============================================================================
// Field Value Renderer
// ============================================================================

interface FieldValueDisplayProps {
  field: IntakeFieldTemplate;
  value: unknown;
}

const FieldValueDisplay = ({ field, value }: FieldValueDisplayProps) => {
  // Handle empty/null values
  if (value === undefined || value === null || value === '') {
    return <EmptyValue>Not provided</EmptyValue>;
  }

  const stringValue = String(value);

  switch (field.type) {
    case 'currency':
      return <FieldValue>{formatCurrency(Number(value))}</FieldValue>;

    case 'percentage':
      return <FieldValue>{formatPercentage(Number(value))}</FieldValue>;

    case 'date':
      return <FieldValue>{formatDate(stringValue)}</FieldValue>;

    case 'ssn':
      return <FieldValue>{formatSSN(stringValue)}</FieldValue>;

    case 'checkbox':
      return <FieldValue>{value ? 'Yes' : 'No'}</FieldValue>;

    case 'select':
    case 'radio': {
      // Find the label for the selected value
      const selectedOption = field.options?.find((opt) => opt.value === value);
      return <FieldValue>{selectedOption ? getI18nString(selectedOption.label) : stringValue}</FieldValue>;
    }

    case 'multi-select': {
      const selectedValues = Array.isArray(value) ? value : [];
      if (selectedValues.length === 0) {
        return <EmptyValue>None selected</EmptyValue>;
      }
      return (
        <ChipGroup>
          {selectedValues.map((val: string) => {
            const option = field.options?.find((opt) => opt.value === val);
            return (
              <Chip key={val} label={option ? getI18nString(option.label) : val} size="small" variant="outlined" />
            );
          })}
        </ChipGroup>
      );
    }

    case 'address': {
      const addr = value as Record<string, string>;
      if (!addr || Object.keys(addr).length === 0) {
        return <EmptyValue>Not provided</EmptyValue>;
      }
      return (
        <AddressValue>
          {addr['street1'] && <Typography variant="body2">{addr['street1']}</Typography>}
          {addr['street2'] && <Typography variant="body2">{addr['street2']}</Typography>}
          <Typography variant="body2">
            {[addr['city'], addr['state'], addr['postalCode']].filter(Boolean).join(', ')}
          </Typography>
        </AddressValue>
      );
    }

    case 'date-range': {
      const range = value as { start?: string; end?: string };
      if (!range?.start && !range?.end) {
        return <EmptyValue>Not provided</EmptyValue>;
      }
      return (
        <FieldValue>
          {range.start ? formatDate(range.start) : '—'} to {range.end ? formatDate(range.end) : '—'}
        </FieldValue>
      );
    }

    case 'rating': {
      const ratingValue = typeof value === 'number' ? value : 0;
      const maxRating = field.validation?.max || 5;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Rating value={ratingValue} max={maxRating} readOnly size="small" />
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            ({ratingValue}/{maxRating})
          </Typography>
        </Box>
      );
    }

    case 'file': {
      const fileValue = value as { name: string; size?: number } | null;
      if (!fileValue?.name) {
        return <EmptyValue>No file uploaded</EmptyValue>;
      }
      return (
        <FieldValue>
          📄 {fileValue.name}
          {fileValue.size && (
            <Typography
              component="span"
              sx={{
                color: 'text.secondary',
                ml: 1,
              }}
            >
              ({(fileValue.size / 1024).toFixed(1)} KB)
            </Typography>
          )}
        </FieldValue>
      );
    }

    case 'signature': {
      const sigValue = value as string | null;
      if (!sigValue) {
        return <EmptyValue>Not signed</EmptyValue>;
      }
      return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, maxWidth: 200 }}>
          <img src={sigValue} alt="Signature" style={{ maxWidth: '100%', height: 'auto' }} />
        </Box>
      );
    }

    case 'color':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: 0.5,
              backgroundColor: stringValue,
              border: 1,
              borderColor: 'divider',
            }}
          />
          <FieldValue>{stringValue}</FieldValue>
        </Box>
      );

    case 'hidden':
      // Don't display hidden fields
      return null;

    case 'phone':
      // Format phone number if it looks like one
      return <FieldValue>{stringValue}</FieldValue>;

    case 'email':
      return (
        <FieldValue>
          <a href={`mailto:${stringValue}`} style={{ color: 'inherit' }}>
            {stringValue}
          </a>
        </FieldValue>
      );

    case 'url':
      return (
        <FieldValue>
          <a href={stringValue} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
            {stringValue}
          </a>
        </FieldValue>
      );

    case 'number':
      return <FieldValue>{Number(value).toLocaleString()}</FieldValue>;

    case 'slider':
      return <FieldValue>{Number(value).toLocaleString()}</FieldValue>;

    case 'textarea':
    case 'rich-text':
      return <FieldValue sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{stringValue}</FieldValue>;

    default:
      return <FieldValue>{stringValue}</FieldValue>;
  }
};

// ============================================================================
// Component
// ============================================================================

export const IntakeSubmissionSummary = ({
  template,
  values,
  neonAccent = NeonAccentKey.NEUTRAL,
  showSections = true,
  showEmptyFields = false,
  className,
  title = 'Your Submission',
}: IntakeSubmissionSummaryProps) => {
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('sm'), { noSsr: true });

  // Evaluate conditional display for a step/field
  const evaluateCondition = (condition: IntakeCondition | undefined): boolean => {
    if (!condition) return true;
    const fieldValue = values[condition.field];
    const conditionValue = condition.value;

    switch (condition.operator) {
      case 'equals':
        return String(fieldValue) === String(conditionValue);
      case 'not_equals':
        return String(fieldValue) !== String(conditionValue);
      case 'contains':
        return String(fieldValue || '').includes(String(conditionValue));
      case 'greater_than':
        return Number(fieldValue) > Number(conditionValue);
      case 'less_than':
        return Number(fieldValue) < Number(conditionValue);
      case 'in':
        return Array.isArray(conditionValue) && (conditionValue as string[]).includes(String(fieldValue));
      case 'not_in':
        return Array.isArray(conditionValue) && !(conditionValue as string[]).includes(String(fieldValue));
      case 'is_empty':
        return !fieldValue || String(fieldValue).trim() === '';
      case 'is_not_empty':
        return Boolean(fieldValue) && String(fieldValue).trim() !== '';
      default:
        return true;
    }
  };

  // Filter visible steps and fields
  const visibleSteps = useMemo(() => {
    return template.data.steps
      .filter((step) => evaluateCondition(step.conditionalDisplay))
      .map((step) => ({
        ...step,
        fields: step.fields.filter((field) => {
          // Check conditional display
          if (!evaluateCondition(field.conditionalDisplay)) return false;
          // Check if field has value (unless showEmptyFields is true)
          if (!showEmptyFields) {
            const value = values[field.name];
            if (value === undefined || value === null || value === '') return false;
          }
          // Skip hidden fields
          if (field.type === 'hidden') return false;
          return true;
        }),
      }))
      .filter((step) => step.fields.length > 0); // Only show steps with visible fields
  }, [template.data.steps, values, showEmptyFields]);

  // Render a single section
  const renderSection = (step: IntakeStepTemplate, index: number) => (
    <SectionContainer key={step.id}>
      {showSections && (
        <>
          <SectionHeader>
            <SectionNumber>{index + 1}</SectionNumber>
            <SectionTitle>{getI18nString(step.name)}</SectionTitle>
          </SectionHeader>
          <Divider sx={{ mb: 2 }} />
        </>
      )}

      <Grid container spacing={isMobile ? 0 : 2}>
        {step.fields.map((field) => (
          <Grid size={{ xs: 12, sm: field.width && field.width <= 6 ? 6 : 12 }} key={field.id}>
            <FieldRow>
              <FieldLabel>{getI18nString(field.label)}</FieldLabel>
              <FieldValueDisplay field={field} value={values[field.name]} />
            </FieldRow>
          </Grid>
        ))}
      </Grid>
    </SectionContainer>
  );

  if (visibleSteps.length === 0) {
    return (
      <GlassCard neonAccent={neonAccent} className={className}>
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <EmptyValue>No submission data available</EmptyValue>
        </Box>
      </GlassCard>
    );
  }

  return (
    <GlassCard neonAccent={neonAccent} className={className}>
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        {title && (
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            {title}
          </Typography>
        )}
        {visibleSteps.map((step, index) => renderSection(step, index))}
      </Box>
    </GlassCard>
  );
};

IntakeSubmissionSummary.displayName = 'IntakeSubmissionSummary';

export default IntakeSubmissionSummary;
